<aside class="pdf-preview-panel">
    <header class="panel-header">
        <div class="panel-heading">
            <ra-icon icon="document-normal" varColor="currentColor" [size]="16" />
            <p class="ra-label-s panel-title">{{ 'document.edit.pdfPreview.title' | translate }}</p>

            <!-- Contagem real de páginas (vem do iframe): o número que o usuário compara
                 com o PDF gerado. Repaginando, a contagem anterior fica (esmaecida) com o
                 spinner ao lado: trocar uma pela outra fazia o cabeçalho mudar de largura a
                 cada tecla. -->
            @let busy = loading() || renderStatus() === 'paginating';
            @if (pageCount() > 0) {
                <span class="panel-pages" [class.stale]="busy">
                    {{ 'document.edit.pdfPreview.pages' | translate: { count: pageCount() } }}
                </span>
            }
            @if (busy) {
                <span class="panel-spinner" aria-hidden="true"></span>
            }

            <ng-content select="[previewHeaderNote]" />
        </div>

        <div class="panel-header-actions">
            <!-- Zoom da folha: - e + para ler melhor; o número volta para a largura do painel. -->
            <div class="panel-zoom" role="group" [attr.aria-label]="'document.edit.pdfPreview.zoom' | translate">
                <button
                    type="button"
                    class="panel-icon-button"
                    [disabled]="!canZoomOut()"
                    [attr.aria-label]="'document.edit.pdfPreview.zoomOut' | translate"
                    [raEditTooltip]="{ text: 'document.edit.pdfPreview.zoomOut' | translate, position: 'bottom' }"
                    (click)="zoomOut()">
                    <ra-icon icon="minus" varColor="currentColor" [size]="14" />
                </button>
                <button
                    type="button"
                    class="panel-zoom-value"
                    [attr.aria-label]="'document.edit.pdfPreview.zoomReset' | translate"
                    [raEditTooltip]="{ text: 'document.edit.pdfPreview.zoomReset' | translate, position: 'bottom' }"
                    (click)="resetZoom()">
                    {{ zoomPercent() }}%
                </button>
                <button
                    type="button"
                    class="panel-icon-button"
                    [disabled]="!canZoomIn()"
                    [attr.aria-label]="'document.edit.pdfPreview.zoomIn' | translate"
                    [raEditTooltip]="{ text: 'document.edit.pdfPreview.zoomIn' | translate, position: 'bottom' }"
                    (click)="zoomIn()">
                    <ra-icon icon="add" varColor="currentColor" [size]="14" />
                </button>
            </div>

            <!-- Baixa o PDF imprimindo a própria prévia (sem Cloud Run). O aviso explica
                 as diferenças em relação ao PDF oficial e aparece SEMPRE. -->
            <button
                type="button"
                class="panel-icon-button"
                [disabled]="renderStatus() !== 'ok'"
                [attr.aria-label]="'document.edit.pdfPreview.download' | translate"
                [raEditTooltip]="{
                    text: 'document.edit.pdfPreview.download' | translate,
                    position: 'bottom'
                }"
                (click)="download()">
                <ra-icon icon="document-download" varColor="currentColor" [size]="16" />
            </button>

            @if (!chatMode()) {
                <button
                    type="button"
                    class="panel-icon-button"
                    [class.active]="expanded()"
                    [attr.aria-pressed]="expanded()"
                    [attr.aria-label]="expandLabelKey() | translate"
                    [raEditTooltip]="{ text: expandLabelKey() | translate, position: 'bottom' }"
                    (click)="toggleExpanded()">
                    <ra-icon icon="maximize-3" varColor="currentColor" [size]="16" />
                </button>

                <button
                    type="button"
                    class="panel-icon-button"
                    [attr.aria-label]="'document.edit.pdfPreview.close' | translate"
                    [raEditTooltip]="{
                        text: 'document.edit.pdfPreview.close' | translate,
                        position: 'bottom'
                    }"
                    (click)="close()">
                    <ra-icon icon="close" varColor="currentColor" [size]="12" />
                </button>
            }
        </div>
    </header>

    <!-- Recalculando: a barra corre sob o cabeçalho e a etiqueta flutua sobre a folha.
         O spinner do cabeçalho sozinho passava despercebido. -->
    @let updating = loading() || renderStatus() === 'paginating';
    @if (updating) {
        <div class="panel-progress" role="progressbar" aria-hidden="true"><span></span></div>
        <div class="panel-updating" role="status" aria-live="polite">
            <span class="panel-spinner" aria-hidden="true"></span>
            {{ 'document.edit.pdfPreview.updating' | translate }}
        </div>
    }

    @if (error()) {
        <div class="panel-error">
            {{ 'document.edit.pdfPreview.error' | translate }}
            <button type="button" class="panel-retry" (click)="retry()">
                {{ 'document.edit.pdfPreview.retry' | translate }}
            </button>
        </div>
    } @else if (renderStatus() === 'partial') {
        <!-- Paginou só uma parte: as folhas na tela são o começo do documento, não ele todo. -->
        <div class="panel-warning" [attr.title]="renderDetail()">
            {{ 'document.edit.pdfPreview.partial' | translate }}
            <button type="button" class="panel-retry" (click)="retry()">
                {{ 'document.edit.pdfPreview.retry' | translate }}
            </button>
        </div>
    } @else if (renderStatus() === 'unpaged' || renderStatus() === 'failed') {
        <!-- Paginação falhou: o conteúdo aparece sem quebras. O detalhe fica no title
             para o diagnóstico não depender do console. -->
        <div class="panel-warning" [attr.title]="renderDetail()">
            {{ 'document.edit.pdfPreview.unpaged' | translate }}
            <button type="button" class="panel-retry" (click)="retry()">
                {{ 'document.edit.pdfPreview.retry' | translate }}
            </button>
        </div>
    }

    <!-- Dois buffers: o ativo na tela, o outro paginando escondido. A troca (com o
         scroll restaurado) acontece só quando o render novo termina, para a folha não
         piscar nem voltar para a capa a cada edição. -->
    <div class="panel-stage">
        @if (srcdocA(); as doc) {
            <iframe
                #frameA
                class="panel-frame"
                [class.behind]="activeSlot() !== 'a'"
                [attr.aria-hidden]="activeSlot() !== 'a' ? 'true' : null"
                [srcdoc]="doc"
                [title]="'document.edit.pdfPreview.title' | translate"></iframe>
        }

        @if (srcdocB(); as doc) {
            <iframe
                #frameB
                class="panel-frame"
                [class.behind]="activeSlot() !== 'b'"
                [attr.aria-hidden]="activeSlot() !== 'b' ? 'true' : null"
                [srcdoc]="doc"
                [title]="'document.edit.pdfPreview.title' | translate"></iframe>
        }

        @if (!activeSlot()) {
            <!-- Esqueleto com a FORMA da folha (logo, título, texto, foto e vídeo), e não
                 um retângulo em branco: enquanto a primeira paginação não chega, o painel
                 já mostra o que vai aparecer ali. -->
            <div class="panel-skeleton" aria-hidden="true">
                <span class="psk psk-logo"></span>
                <span class="psk psk-title"></span>
                <span class="psk psk-line psk-line--label"></span>
                <span class="psk psk-line"></span>
                <span class="psk psk-line psk-line--short"></span>

                <span class="psk psk-media">
                    <span class="psk-glyph psk-glyph--image"></span>
                </span>

                <span class="psk psk-line"></span>
                <span class="psk psk-line psk-line--short"></span>
            </div>
        }

        <ng-content select="[previewOverlay]" />
    </div>
</aside>
